<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representative" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium">
          {{ $t('representative.representative') }} - {{ representative.name }}
        </label>
      </div>

      <div
        :class="{
          'pointer-events-none': !modifiedData.isFromManager || viewOnly || mode === 'assign',
        }">
        <label for="remoteCode" class="text-sm font-medium">{{
          $t('representative.customerRemotedCode')
        }}</label>
        <BaseInput
          v-model="modifiedData.customerRemotedCode"
          color="base"
          show-error-as-tooltip
          :error="
            $v.customerRemotedCode.required.$invalid
              ? $v.customerRemotedCode.required.$message
              : $v.customerRemotedCode.uniqueRemoteCode.$invalid
                ? $v.customerRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <BaseSearchDropdown
        :list-items="customerItems"
        v-model="selectedCustomer"
        :disabled="!modifiedData.isFromManager || viewOnly"
        :placeholder="$t('representative.customer')"
        :no-results-text="$t('messages.noResultsText')"
        @item-selected="handleCustomerSelection"
        @update-search="handleCustomerSearch"
        @clear-selection="handleCustomerClear"
        show-error-as-tooltip
        :error="$v.customerId.required.$invalid ? $v.customerId.required.$message : ''"
        :count="customerCount" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        light
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSearchDropdown from '@/components/common/BaseSearchDropdown.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import type { DCCustomer } from '@/types/DataCollection';
import type { Customer, Representative } from '@/types/Traders';
import type { SelectDropdownItem } from '@/types/Base';
import { RecordMode } from '@/types/Base';
import { deepCopy } from '@/utils/data';
import { getRepresentativeById } from '@/services/api/traders';
import { getCustomersWithConditions } from '@/services/api/traders';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

const { t: $t } = useI18n();

const props = defineProps<{
  customer: DCCustomer;
  mode: 'add' | 'edit' | 'view' | 'assign';
  viewOnly?: boolean;
  representativeId: string;
  existingCustomers?: DCCustomer[];
}>();

const emit = defineEmits<{
  closeModal: [];
  save: [customer: DCCustomer];
}>();

const modifiedData = ref<DCCustomer>(deepCopy(props.customer));
const originalData = ref<DCCustomer>(deepCopy(props.customer));
const representative = ref<Representative>();
const customerItems = ref<SelectDropdownItem[]>([]);
const selectedCustomer = ref<SelectDropdownItem | null>(null);
const customerCount = ref(0);

const isModified = computed(() => {
  return JSON.stringify(modifiedData.value) !== JSON.stringify(originalData.value);
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingCustomers) return true;

  const existingDuplicate = props.existingCustomers.find(
    (customer) =>
      customer.id !== modifiedData.value.id &&
      customer.customerRemotedCode === value &&
      customer.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = computed(() => ({
  customerId: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  customerRemotedCode: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    uniqueRemoteCode: helpers.withMessage(
      `${$t('messages.errors.duplicateRemoteCode')}`,
      isUniqueRemoteCode
    ),
  },
}));

const validationModel = computed(() => ({
  customerId: modifiedData.value.customerId || '',
  customerRemotedCode: modifiedData.value.customerRemotedCode || '',
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => props.customer,
  (newCustomer) => {
    modifiedData.value = deepCopy(newCustomer);
    originalData.value = deepCopy(newCustomer);
    setSelectedCustomer();
  },
  { immediate: true }
);

onMounted(async () => {
  await loadRepresentative();
  await loadCustomers();
  setSelectedCustomer();
});

async function loadRepresentative() {
  if (props.representativeId) {
    const res = await getRepresentativeById(props.representativeId);
    if (res) {
      representative.value = res;
    }
  }
}

async function loadCustomers(searchQuery = '', page = 1) {
  const res = await getCustomersWithConditions({
    page,
    itemsPerPage: 50,
    query: searchQuery,
  });
  if (res) {
    customerCount.value = res.count;
    const newItems = res.data.map((customer: Customer) => ({
      value: customer.id,
      name: customer.trader?.name || '',
      displayName: `${customer.trader?.name || ''}\n${customer.code || ''}\n${customer.profession || ''}`,
      id: customer.id,
      traderName: customer.trader?.name || '',
      code: customer.code || '',
      profession: customer.profession || '',
    }));

    if (page === 1) {
      customerItems.value = newItems;
    } else {
      customerItems.value = [...customerItems.value, ...newItems];
    }
  }
}

async function handleCustomerSearch(searchQuery: string, page: number) {
  await loadCustomers(searchQuery, page);
}

function handleCustomerClear() {
  selectedCustomer.value = null;
  modifiedData.value.customerId = '';
  modifiedData.value.customer = {};
}

function setSelectedCustomer() {
  if (props.customer.customer && props.customer.customer.id) {
    selectedCustomer.value = {
      value: props.customer.customer.id,
      name: props.customer.customer.trader?.name || '',
      displayName: `${props.customer.customer.trader?.name || ''}\n${props.customer.customer.code || ''}\n${props.customer.customer.profession || ''}`,
    };
    return;
  }
  if (modifiedData.value.customerId && customerItems.value.length > 0) {
    selectedCustomer.value =
      customerItems.value.find((c) => c.value === modifiedData.value.customerId) || null;
  }
}

function handleCustomerSelection(customer: SelectDropdownItem | null) {
  selectedCustomer.value = customer;
  if (customer) {
    modifiedData.value.customerId = String(customer.value);
    modifiedData.value.customer = {
      id: String(customer.value),
      traderName: (customer as any).traderName || '',
      trader: {
        id: String(customer.value),
        name: (customer as any).traderName || '',
        code: (customer as any).code || '',
      },
      profession: (customer as any).profession || '',
      code: (customer as any).code || '',
    };
  } else {
    modifiedData.value.customerId = '';
    modifiedData.value.customer = {
      id: '',
      traderName: '',
      trader: {
        id: '',
        name: '',
        code: '',
      },
      profession: '',
      code: '',
    };
  }
}

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    return;
  }
  emit('save', modifiedData.value);
}

function onDismissChanges() {
  emit('closeModal');
}

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.customer')}`;
}
</script>
